<template>
    <div class="mb-8">
        <!-- Travel Portal Toggle Section -->
        <div class="flex items-center gap-8">
            <div class="flex flex-col justify-center items-start gap-1 flex-1">
                <h4 class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                    I booked using a travel portal
                </h4>
                <p class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                    Keep a record of the portal you used to make your booking.
                </p>
            </div>
            <div>
                <Switch
                    :value="usesTravelPortal"
                    @input="toggleTravelPortal"
                    label="Use Travel Portal"
                />
            </div>
        </div>

        <!-- Travel Portal Selector - Appears when toggle is on -->
        <transition name="fade">
            <div v-if="usesTravelPortal" class="mt-4 flex flex-col gap-3">
                <div
                    class="flex p-4 flex-col items-start gap-6 self-stretch rounded-lg bg-slate-50 dark:bg-slate-900/40"
                >
                    <div class="flex flex-col items-start self-stretch">
                        <label
                            class="pb-2 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                        >
                            Travel portal
                        </label>

                        <div class="relative w-full">
                            <!-- Dropdown toggle button -->
                            <button
                                @click="toggleDropdown"
                                class="flex py-[10px] px-4 items-center gap-2 self-stretch w-full rounded border bg-white dark:bg-slate-800"
                                :class="{
                                    'border-slate-200 dark:border-slate-700': !hasIdError,
                                    'border-red-500': hasIdError,
                                }"
                                type="button"
                            >
                                <span
                                    v-if="selectedPortal"
                                    class="flex items-center gap-2"
                                >
                                    <travel-portal-icon
                                        :portal="selectedPortal.code"
                                        class="w-5 h-5"
                                    />
                                    <span class="text-slate-800 dark:text-slate-100">
                                        {{
                                            selectedPortal.code === 'other' &&
                                            customPortalName
                                                ? customPortalName
                                                : selectedPortal.name
                                        }}
                                    </span>
                                </span>
                                <span
                                    v-else
                                    class="text-slate-400 dark:text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                                >
                                    Select travel portal
                                </span>

                                <svg
                                    class="w-5 h-5 ml-auto"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clip-path="url(#clip0_896_8368)">
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M6.07611 7.74408C6.40155 7.41864 6.92918 7.41864 7.25462 7.74408L9.9987 10.4882L12.7428 7.74408C13.0682 7.41864 13.5958 7.41864 13.9213 7.74408C14.2467 8.06951 14.2467 8.59715 13.9213 8.92259L11.1772 11.6667C10.5263 12.3175 9.47106 12.3175 8.82019 11.6667L6.07611 8.92259C5.75067 8.59715 5.75067 8.06951 6.07611 7.74408Z"
                                            fill="#64748B"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_896_8368">
                                            <rect
                                                width="20"
                                                height="20"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>

                            <!-- Dropdown menu -->
                            <div
                                v-if="dropdownOpen"
                                class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border rounded-md shadow-sm"
                            >
                                <!-- Debug info -->
                                <div
                                    v-if="tripsStore.loading"
                                    class="p-3 text-center text-slate-500 dark:text-slate-400"
                                >
                                    Loading...
                                </div>
                                <div
                                    v-else-if="sortedPortals.length === 0"
                                    class="p-3 text-center text-slate-500 dark:text-slate-400"
                                >
                                    No portals available
                                </div>
                                <!-- End debug info -->

                                <ul class="py-1 max-h-60 overflow-auto">
                                    <li
                                        v-for="portal in sortedPortals"
                                        :key="portal.id"
                                        @click="selectPortal(portal)"
                                        class="flex px-2 py-2 items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer rounded-md"
                                    >
                                        <travel-portal-icon
                                            :portal="portal.code"
                                        />
                                        <span class="text-slate-800 dark:text-slate-100 text-sm">
                                            {{ portal.name }}
                                        </span>
                                    </li>
                                </ul>
                            </div>
                            <div
                                v-if="hasIdError"
                                class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                            >
                                <TripIcon icon="caution" />

                                {{ errorMessage }}
                            </div>
                        </div>
                    </div>

                    <!-- Custom portal name field - Appears when "Other" is selected -->
                    <div
                        v-if="selectedPortal && selectedPortal.code === 'other'"
                        class="flex flex-col items-start self-stretch h-auto"
                    >
                        <label
                            class="pb-2 flex items-start gap-2.5 self-stretch text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                        >
                            Name
                        </label>
                        <input
                            type="text"
                            v-model="customPortalName"
                            placeholder="Travel portal name"
                            class="flex py-[10px] px-4 items-center gap-2 self-stretch rounded border bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-normal leading-5"
                            :class="{
                                'border-slate-200 dark:border-slate-700': !hasNameError,
                                'border-red-500': hasNameError,
                            }"
                            @input="updateCustomPortalName"
                        />
                        <div
                            v-if="hasNameError"
                            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                        >
                            <TripIcon icon="caution" />

                            {{ errorMessage }}
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, onBeforeUnmount, computed } from 'vue';
import TripIcon from '@/components/trips/icons.vue';
import Switch from '@/components/ui/switch/switch.vue';
import TravelPortalIcon from '@/components/ui/icon/travel-portal-icon.vue';
import { useTripsStore } from '@/store/trips';

// Props
const props = defineProps({
    redemption: {
        type: Object,
        required: true,
    },
    errors: {
        type: Object,
        required: false,
    },
});

// Emits
const emit = defineEmits([
    'update:travelPortal',
    'update:travelPortalName',
    'update:usedTravelPortal',
]);

// Store
const tripsStore = useTripsStore();

// Reactive state
const usesTravelPortal = ref(!!props.redemption.travel_portal_id);
const selectedPortal = ref(null);
const dropdownOpen = ref(false);
const customPortalName = ref(props.redemption.travel_portal_custom_name || '');

// Computed properties
const hasIdError = computed(() => {
    return !!props.errors?.travel_portal_id;
});

const hasNameError = computed(() => {
    return !!props.errors?.travel_portal_custom_name;
});

const errorMessage = computed(() => {
    return (
        props.errors?.travel_portal_id ||
        props.errors?.travel_portal_custom_name ||
        'Please choose a travel portal'
    );
});

const sortedPortals = computed(() => {
    if (!tripsStore.travelPortals || tripsStore.travelPortals.length === 0) {
        return [];
    }

    // Sort portals by order
    return [...tripsStore.travelPortals].sort((a, b) => {
        return a.order - b.order;
    });
});

const handleClickOutside = (event) => {
    if (dropdownOpen.value && !event.target.closest('.relative')) {
        dropdownOpen.value = false;
    }
};

// Lifecycle hooks
onMounted(() => {
    document.addEventListener('click', handleClickOutside);

    // Get travel portals from backend
    tripsStore.getTravelPortals();

    if (props.redemption.travel_portal_id) {
        usesTravelPortal.value = true;

        if (
            props.redemption.travel_portal_id &&
            props.redemption.travel_portal
        ) {
            if (
                props.redemption.travel_portal.code === 'other' &&
                props.redemption.travel_portal_custom_name
            ) {
                customPortalName.value =
                    props.redemption.travel_portal_custom_name;
            }
        }
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});

// Watchers
watch(
    () => tripsStore.travelPortals,
    () => {
        if (props.redemption.travel_portal_id) {
            loadSelectedPortal();
        }
    },
    { deep: true }
);

// Methods
const loadSelectedPortal = () => {
    if (tripsStore.travelPortals.length === 0) return;

    const portalId = props.redemption.travel_portal_id;
    if (!portalId) return;

    const foundPortal = tripsStore.travelPortals.find(
        (portal) => portal.id === portalId
    );
    if (foundPortal) {
        selectedPortal.value = { ...foundPortal };

        if (
            foundPortal.code === 'other' &&
            props.redemption.travel_portal_custom_name
        ) {
            customPortalName.value = props.redemption.travel_portal_custom_name;
        }
    }
};

const toggleDropdown = () => {
    dropdownOpen.value = !dropdownOpen.value;
};

const toggleTravelPortal = (value) => {
    usesTravelPortal.value = value;

    if (!value) {
        selectedPortal.value = null;
        customPortalName.value = '';
        emit('update:travelPortal', null);
    } else if (selectedPortal.value) {
        const portalToEmit = preparePortalForEmit(selectedPortal.value);
        emit('update:travelPortal', portalToEmit);
    }

    emit('update:usedTravelPortal', value);
};

const preparePortalForEmit = (portal) => {
    const portalToEmit = { ...portal };

    // Always include travel_portal_custom_name in the emission
    if (portal.code === 'other') {
        // If it's "Other", use the custom name entered by the user
        portalToEmit.travel_portal_custom_name = customPortalName.value;
    } else {
        // If it's not "Other", make sure the field is empty
        portalToEmit.travel_portal_custom_name = '';
    }

    return portalToEmit;
};

const selectPortal = (portal) => {
    selectedPortal.value = { ...portal };
    dropdownOpen.value = false;

    if (portal.code !== 'other') {
        customPortalName.value = '';
    }

    const portalToEmit = preparePortalForEmit(portal);
    emit('update:travelPortal', portalToEmit);
};

const updateCustomPortalName = () => {
    if (!selectedPortal.value || selectedPortal.value.code !== 'other') return;

    const portalToEmit = { ...selectedPortal.value };
    portalToEmit.travel_portal_custom_name = customPortalName.value;

    emit('update:travelPortalName', portalToEmit);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
